export { registerConnectInstallation, type ConnectInstallationCredential } from "./installation.ts";
import * as Schema from "effect/Schema";
import {
  CloudWorkspace,
  CloudAllowanceStatus,
  CloudAccountAllowance,
  CloudAllowanceHistory,
  type AdjustCloudAllowance,
  type ChangeCloudSubscription,
  type CreateCloudWorkspace,
  type CloudWorkspaceAction,
  FeedbackCreatedResponseSchema,
  type FeedbackSubmission,
  PublicContributionProject,
  PublicContributionProjectPage,
  PublishedProjectContribution,
  type CreatePublicContributionProjectInput,
  type ProjectContributionSnapshot,
} from "@afar-research/contracts";
import { ConnectError, connectCall, type ConnectFetch } from "@afar-research/shared/connect";

/**
 * AFAR Connect's account surface: the account's installations, Cloud Workspaces, Usage Allowance,
 * and feedback. Every call here goes to AFAR Connect.
 *
 * Reaching a Cloud Workspace's Environment is not one of them: `openNetworkConnection` asks for the
 * introduction, and the Environment authorizes its own clients (ADR-0122, ADR-0150).
 */
export function connectClient(input: { connectOrigin: string; fetch: ConnectFetch }) {
  return {
    contributionProject: (token: string, projectId: string) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        `/v1/contributions/${encodeURIComponent(projectId)}`,
        token,
        undefined,
        Schema.NullOr(PublicContributionProjectPage),
      ),
    contributionProjects: (token: string) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        "/v1/contributions/projects",
        token,
        undefined,
        Schema.Array(PublicContributionProject),
      ).then((projects) =>
        projects.map((project) => ({
          ...project,
          publicUrl: new URL(project.publicUrl, input.connectOrigin).href,
        })),
      ),
    createContributionProject: (token: string, body: CreatePublicContributionProjectInput) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        "/v1/contributions/projects",
        token,
        body,
        PublicContributionProject,
      ).then((project) => ({
        ...project,
        publicUrl: new URL(project.publicUrl, input.connectOrigin).href,
      })),
    publishProjectContribution: (
      token: string,
      projectId: string,
      attemptId: string,
      snapshot: ProjectContributionSnapshot,
    ) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        `/v1/contributions/${encodeURIComponent(projectId)}/publish`,
        token,
        { attemptId, snapshot },
        PublishedProjectContribution,
      ),
    withdrawProjectContribution: (token: string, projectId: string, snapshotId?: string) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        `/v1/contributions/${encodeURIComponent(projectId)}/withdraw${snapshotId ? `/${encodeURIComponent(snapshotId)}` : ""}`,
        token,
        {},
        Schema.Struct({ withdrawn: Schema.Boolean }),
      ),
    cloudAllowance: (token: string) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        "/v1/allowance",
        token,
        undefined,
        CloudAllowanceStatus,
      ),
    cloudAccounts: (token: string, search: string) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        `/v1/admin/accounts?search=${encodeURIComponent(search)}`,
        token,
        undefined,
        Schema.Array(CloudAccountAllowance),
      ),
    cloudAllowanceHistory: (token: string, accountId: string) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        `/v1/admin/history?accountId=${encodeURIComponent(accountId)}`,
        token,
        undefined,
        Schema.Array(CloudAllowanceHistory),
      ),
    adjustCloudAllowance: (token: string, body: AdjustCloudAllowance) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        "/v1/admin/allowance",
        token,
        body,
        Schema.Struct({ recorded: Schema.Boolean }),
      ),
    changeCloudSubscription: (token: string, body: ChangeCloudSubscription) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        "/v1/admin/subscription",
        token,
        body,
        Schema.Struct({ recorded: Schema.Boolean }),
      ),
    cloudWorkspaces: (token: string) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        "/v1/workspaces",
        token,
        undefined,
        Schema.Array(CloudWorkspace),
      ),
    createCloudWorkspace: (token: string, body: CreateCloudWorkspace) =>
      connectCall(input.fetch, input.connectOrigin, "/v1/workspaces", token, body, CloudWorkspace),
    cloudWorkspaceAction: (token: string, body: CloudWorkspaceAction) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        `/v1/workspaces/${body.action}`,
        token,
        {
          id: body.id,
          expectedRevision: body.expectedRevision,
          requestId: body.requestId,
          ...(body.size === undefined ? {} : { size: body.size }),
          ...(body.diskGb === undefined ? {} : { diskGb: body.diskGb }),
        },
        Schema.Unknown,
      ),
    installations: (accountToken: string) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        "/v1/installations",
        accountToken,
        undefined,
        Schema.Array(Schema.Struct({ installationId: Schema.String })),
      ),
    revokeInstallation: (accountToken: string, installationId: string) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        "/v1/installations/revoke",
        accountToken,
        { installationId },
        Schema.Struct({ revoked: Schema.Boolean }),
      ),
    /**
     * Creates a Feedback Issue through Connect. The submitter's account authenticates the call
     * and Connect mints the GitHub credential; nothing GitHub-shaped reaches a client (ADR-0105).
     * The idempotency key is the client's own, so a manual retry after a lost response returns the
     * original reference rather than filing the report twice.
     */
    submitFeedback: (accountToken: string, payload: FeedbackSubmission) =>
      connectCall(
        input.fetch,
        input.connectOrigin,
        "/v1/feedback",
        accountToken,
        payload,
        FeedbackCreatedResponseSchema,
      ),
  };
}
export {
  signInConnect,
  refreshConnectToken,
  connectSignInScopes,
  readConnectIdentity,
  connectSignInTimeoutMs,
  type ConnectTokens,
} from "./signIn.ts";

/** Do not report successful sign-out until revocation is acknowledged. Retrying is idempotent. */
export async function signOutConnect(input: {
  fetch: ConnectFetch;
  connectOrigin: string;
  credential: string;
  clearCredential: () => Promise<void>;
}) {
  await connectCall(
    input.fetch,
    input.connectOrigin,
    "/v1/sign-out",
    input.credential,
    {},
    Schema.Struct({ revoked: Schema.Boolean }),
  ).catch((error: unknown) => {
    // Connect no longer knows the credential — revoked elsewhere, or its account gone — so there
    // is nothing left to revoke, and keeping it would leave the person signed in to nothing with
    // no way out. Any other failure may leave a live credential behind, so it stays visible.
    if (error instanceof ConnectError && (error.status === 401 || error.status === 403)) return;
    throw error;
  });
  await input.clearCredential();
}
export { makeBrowserConnect, browserConnectStorageKey } from "./browser.ts";
export { connectOperationQueue } from "./serial.ts";

export {
  connectAccountChanged,
  connectAccounts,
  readConnectProfile,
  withConnectAccountToken,
} from "./accounts.ts";
export {
  cloudWorkspaceConnectOutcome,
  cloudWorkspaceWebSocketBaseUrl,
} from "./cloudWorkspaceConnection.ts";
export { networkConnectTarget, openNetworkConnection } from "./networkConnection.ts";
export { computerInstallCommands, quoteComputerShell } from "./computerCommands.ts";
export { submitFeedbackDraft } from "./feedback.ts";
